import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
} from 'lucide-react';
import {
  buildAdmissionsTuitionContractViewModel,
  formatAdmissionsTuitionContractDate,
  formatAdmissionsTuitionContractGradeLabel,
  getAdmissionsTuitionContractVersions,
  type AdmissionsTuitionChildConfiguration,
  type AdmissionsTuitionConfiguration,
  type AdmissionsTuitionContractSchoolInfo,
  type AdmissionsTuitionContractVersion,
  type AdmissionsTuitionContractViewModel,
  type AdmissionsTuitionFeeLine,
  type AdmissionsTuitionResolution,
} from '@triedge/students-families-domain';
import { TailwindDateField } from '../../../../components/forms/TailwindDateField';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';
import {
  useGetSettingsModuleConfigQuery,
  useUpdateAdmissionsApplicationMutation,
} from '../../../../state/domainRouterApi';
import {
  useDomainRouterMutation,
  useDomainRouterQuery,
} from '../../../../state/domainRouterHooks';
import { parseSettingsState } from '../../../../settings/settingsState';
import { isFinancialAidAccessEnabledForApplication } from '../../../../shared/financialAidAccess';
import {
  approveAdmissionsTuitionConfiguration,
  buildAdmissionsTuitionConfiguration,
  findAdmissionsTuitionFee,
  formatTuitionCurrency,
  isAdmissionsTuitionConfigurationApproved,
  parseCurrencyInputToCents,
  updateAdmissionsTuitionConfiguration,
  updateAdmissionsTuitionPaymentPlan,
  updateAdmissionsTuitionRateCategory,
} from './tuitionUtils';
import { buildAdmissionsTuitionContractPdfFromModel } from '../../utils/admissionsTuitionContractPdf';

type DetailOutletContext = {
  applicationId: string;
  detailStatus?: string;
  financialAidAccessEnabled?: boolean;
  onFinancialAidAccessChange?: (enabled: boolean) => void;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const unwrapMutationResult = async (mutationResult: unknown): Promise<unknown> => {
  if (
    mutationResult &&
    typeof mutationResult === 'object' &&
    typeof (mutationResult as { unwrap?: unknown }).unwrap === 'function'
  ) {
    return (mutationResult as { unwrap: () => Promise<unknown> }).unwrap();
  }
  const result = await mutationResult;
  const resultRecord = asRecord(result);
  if ('error' in resultRecord) {
    throw resultRecord.error;
  }
  if ('data' in resultRecord) {
    return resultRecord.data;
  }
  return result;
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const direct = normalizeText(record.message);
    if (direct) {
      if (direct.includes('unrecognized_keys') || direct.includes('Unrecognized key')) {
        return 'The tuition draft could not be saved. Refresh the page and try again.';
      }
      return direct;
    }
    const nested = normalizeText(asRecord(record.data).message);
    if (nested) {
      if (nested.includes('unrecognized_keys') || nested.includes('Unrecognized key')) {
        return 'The tuition draft could not be saved. Refresh the page and try again.';
      }
      return nested;
    }
  }
  return fallback;
};

const formatStatusLabel = (value: string | null | undefined): string =>
  normalizeText(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ') || 'Unknown';

const mergeContractHistoryFromSubmittedDraft = (
  savedConfiguration: AdmissionsTuitionConfiguration,
  submittedConfiguration: AdmissionsTuitionConfiguration,
): AdmissionsTuitionConfiguration => {
  const submittedVersions = getAdmissionsTuitionContractVersions(submittedConfiguration);
  if (submittedVersions.length === 0) {
    return savedConfiguration;
  }

  const mergedVersions = [...getAdmissionsTuitionContractVersions(savedConfiguration)];
  for (const version of submittedVersions) {
    const versionKey = `${normalizeText(version.version)}:${normalizeText(version.documentId)}`;
    const alreadyPresent = mergedVersions.some((entry) =>
      `${normalizeText(entry.version)}:${normalizeText(entry.documentId)}` === versionKey
    );
    if (!alreadyPresent) {
      mergedVersions.push(version);
    }
  }

  if (mergedVersions.length === (savedConfiguration.contract.versions ?? []).length) {
    return savedConfiguration;
  }

  return {
    ...savedConfiguration,
    contract: {
      ...savedConfiguration.contract,
      versions: mergedVersions,
    },
  };
};

const sanitizeTuitionCategoryDescription = (value: unknown): string => {
  const description = normalizeText(value);
  if (!description) {
    return '';
  }
  const normalized = description.toLowerCase();
  if (
    normalized.includes('materialized')
    || normalized.includes('tuition rows')
    || normalized.includes('legacy admissions')
  ) {
    return 'Review the selected tuition defaults, fees, discounts, and due dates before approving this draft.';
  }
  return description;
};

const readSettingsPayload = (value: unknown) =>
  parseSettingsState(asRecord(value).payload);

type SettingsStateSnapshot = ReturnType<typeof readSettingsPayload>;

type StaffTuitionContractPreviewModel = AdmissionsTuitionContractViewModel;

const formatContractDate = formatAdmissionsTuitionContractDate;
const formatContractGradeLabel = formatAdmissionsTuitionContractGradeLabel;

const formatAddress = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }
  const record = asRecord(value);
  return [
    normalizeText(record.street),
    [normalizeText(record.city), normalizeText(record.state), normalizeText(record.zip ?? record.zipCode)]
      .filter(Boolean)
      .join(', ')
      .replace(/, ([^,]+)$/, ' $1'),
  ].filter(Boolean).join(', ');
};

const resolveSettingsSchoolInfo = (settingsState: SettingsStateSnapshot) => {
  const organization = asRecord(settingsState.organization);
  const organizationExt = asRecord(organization.ext);
  const divisionContacts = asRecord(organizationExt.divisionContacts);
  const businessOffice = asRecord(divisionContacts.businessOffice);
  const email =
    normalizeText(businessOffice.email)
    || normalizeText(organization.emailSupport)
    || normalizeText(organization.emailAdmissions);
  const phone = normalizeText(businessOffice.phone) || normalizeText(organization.phone);
  const phoneExt = normalizeText(asRecord(businessOffice.extensions).operationsManager)
    || normalizeText(organization.phoneExt);
  return {
    schoolName:
      normalizeText(organization.name)
      || normalizeText(organization.shortName)
      || 'School',
    schoolLogo: normalizeText(organization.logo) || null,
    schoolAddress:
      formatAddress(businessOffice.address)
      || formatAddress(organization.address),
    schoolPhone: [phone, phoneExt].filter(Boolean).join(' '),
    schoolEmail: email,
  };
};

const getMissingContractSchoolInfoFields = (
  schoolInfo: AdmissionsTuitionContractSchoolInfo,
): string[] => {
  const missing: string[] = [];
  if (!normalizeText(schoolInfo.schoolName)) missing.push('school name');
  if (!normalizeText(schoolInfo.schoolAddress)) missing.push('school address');
  if (!normalizeText(schoolInfo.schoolEmail)) missing.push('school email');
  return missing;
};

const resolveFamilyContractAddressLines = (application: unknown): string[] => {
  const applicant = asRecord(asRecord(application).applicant);
  const primaryContact = asRecord(applicant.primaryContact);
  const primaryAddress = asRecord(primaryContact.address);
  const parents = asArray<Record<string, unknown>>(applicant.parents);
  const fallbackParentAddress = asRecord(
    parents.map((parent) => asRecord(parent.address)).find((address) => normalizeText(address.street)),
  );
  const address = normalizeText(primaryAddress.street) ? primaryAddress : fallbackParentAddress;
  const street = normalizeText(address.street);
  const cityStateZip = [
    normalizeText(address.city),
    normalizeText(address.state),
    normalizeText(address.zipCode ?? address.zip),
  ].filter(Boolean).join(', ').replace(/, ([^,]+)$/, ' $1');
  return [street, cityStateZip].filter(Boolean);
};

const resolveParentSalutation = (
  application: unknown,
  fallbackFamilyName: string,
): string => {
  const applicant = asRecord(asRecord(application).applicant);
  const primaryContact = asRecord(applicant.primaryContact);
  const primaryName = [normalizeText(primaryContact.firstName), normalizeText(primaryContact.lastName)]
    .filter(Boolean)
    .join(' ');
  if (primaryName) return primaryName;
  const parents = asArray<Record<string, unknown>>(applicant.parents);
  const parentName = parents
    .map((parent) => [normalizeText(parent.firstName), normalizeText(parent.lastName)].filter(Boolean).join(' '))
    .find(Boolean);
  return parentName || fallbackFamilyName || 'Parent/Guardian';
};

const buildStaffTuitionContractPreviewModel = ({
  configuration,
  application,
  familyName,
  schoolInfo,
}: {
  configuration: AdmissionsTuitionConfiguration;
  application: unknown;
  familyName: string;
  schoolInfo: AdmissionsTuitionContractSchoolInfo;
}): StaffTuitionContractPreviewModel => {
  return buildAdmissionsTuitionContractViewModel({
    configuration,
    parentSalutation: resolveParentSalutation(application, familyName),
    familyAddressLines: resolveFamilyContractAddressLines(application),
    schoolInfo,
  });
};

const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
};

const createImageObjectUrlFromDataUrl = (dataUrl: string): string | null => {
  const match = dataUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);
  if (!match?.[1] || !match[2] || typeof window.atob !== 'function') {
    return null;
  }
  try {
    const binary = window.atob(match[2]);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return URL.createObjectURL(new Blob([bytes], { type: match[1] }));
  } catch {
    return null;
  }
};

const escapeContractHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildStaffTuitionContractPrintHtml = (
  model: StaffTuitionContractPreviewModel,
  logoUrl: string | null,
): string => {
  const logoMarkup = logoUrl
    ? `<img class="school-logo" src="${escapeContractHtml(logoUrl)}" alt="" />`
    : `<div class="school-logo-fallback">${escapeContractHtml(model.schoolName.slice(0, 2).toUpperCase())}</div>`;
  const familyAddress = model.familyAddressLines.length
    ? model.familyAddressLines.map((line) => `<div>${escapeContractHtml(line)}</div>`).join('')
    : '<div>Address on file</div>';
  const studentRows = model.studentRows.map((row) => `
        <tr>
          <td>${escapeContractHtml(row.name)}</td>
          <td>${escapeContractHtml(row.gradeLabel)}</td>
          <td class="money">${escapeContractHtml(row.tuitionLabel)}</td>
        </tr>`).join('');
  const feeRows = model.feeRows.length
    ? model.feeRows.map((row) => `
        <tr>
          <td>${escapeContractHtml(row.label)}</td>
          <td>${escapeContractHtml(row.scheduleLabel)}</td>
          <td class="money">${escapeContractHtml(row.amountLabel)}</td>
        </tr>`).join('')
    : `
        <tr>
          <td colspan="3" class="empty-row">No additional fees configured.</td>
        </tr>`;
  const totalRows = [
    ['Tuition subtotal', model.tuitionSubtotalLabel],
    ['Multi-child discount', model.multiChildDiscountLabel],
    ['Staff / Chinuch discount', model.staffChinuchDiscountLabel],
    ['Total tuition', model.totalTuitionLabel],
    ['Total fees', model.totalFeesLabel],
    ['Financial aid credit', model.financialAidCreditLabel],
    ['Fundraising obligation', model.fundraisingObligationLabel],
    ['Total obligation', model.totalObligationLabel],
  ].map(([label, value]) => `
        <div class="total-row${label === 'Total obligation' ? ' total-row-strong' : ''}">
          <span>${escapeContractHtml(label)}</span>
          <strong>${escapeContractHtml(value)}</strong>
        </div>`).join('');

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Tuition Contract - ${escapeContractHtml(model.academicYear)}</title>
    <style>
      :root { color-scheme: light; }
      * { box-sizing: border-box; }
      html, body {
        margin: 0;
        min-height: 100%;
        background: #e8eeef;
        color: #243235;
        font-family: Inter, "Helvetica Neue", Arial, sans-serif;
        font-size: 10.5px;
        line-height: 1.34;
      }
      body { padding: 20px; }
      .print-sheet {
        width: 8.5in;
        min-height: 11in;
        margin: 0 auto;
        padding: 0.45in;
        background: #ffffff;
        box-shadow: 0 18px 48px rgba(36, 50, 53, 0.22);
      }
      .letterhead {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        border-bottom: 2px solid #1c3b3e;
        padding-bottom: 10px;
      }
      .school-block {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
      }
      .school-logo,
      .school-logo-fallback {
        width: 46px;
        height: 46px;
        border: 1px solid #d7e5e7;
        border-radius: 8px;
        background: #ffffff;
        object-fit: contain;
        padding: 5px;
        flex: 0 0 auto;
      }
      .school-logo-fallback {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #2697a2;
        background: #effafa;
        font-weight: 800;
        font-size: 13px;
      }
      .school-name {
        margin: 0;
        color: #1c3b3e;
        font-size: 18px;
        line-height: 1.1;
        font-weight: 800;
      }
      .school-meta,
      .document-meta,
      .muted {
        color: #71807e;
      }
      .document-meta {
        text-align: right;
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }
      h1 {
        margin: 14px 0 10px;
        color: #1f2f32;
        font-size: 24px;
        line-height: 1.05;
      }
      .intro-grid,
      .signature-section {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
      }
      .info-box,
      .totals-box {
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 9px 10px;
        background: #fbfcfc;
      }
      .label {
        margin: 0 0 5px;
        color: #8b9796;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      .value {
        color: #243235;
        font-size: 12px;
        font-weight: 750;
      }
      .section-title {
        margin: 13px 0 6px;
        background: #1c3b3e;
        color: #ffffff;
        padding: 5px 8px;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        border: 1px solid #e5e7eb;
      }
      th {
        background: #f4f7f7;
        color: #71807e;
        font-size: 9px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      th,
      td {
        border-bottom: 1px solid #edf0f0;
        padding: 5px 8px;
        text-align: left;
      }
      tr {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .money {
        text-align: right;
        white-space: nowrap;
        font-weight: 750;
      }
      .empty-row {
        color: #71807e;
        text-align: center;
      }
      .totals-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 5px 8px;
      }
      .total-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        border-radius: 7px;
        background: #f8faf9;
        padding: 6px 8px;
      }
      .total-row-strong {
        border: 1px solid #bce4e7;
        background: #edfafa;
        color: #1d7d86;
        font-size: 12px;
      }
      .closing-section {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .signature-section {
        gap: 24px;
        margin-top: 36px;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .signature-field {
        min-width: 0;
      }
      .signature-line,
      .printed-name-line {
        border-bottom: 1px solid #1f2f32;
      }
      .signature-line {
        min-height: 44px;
      }
      .printed-name-line {
        min-height: 26px;
        margin-top: 10px;
      }
      .signature-label,
      .printed-name-label {
        padding-top: 5px;
        color: #71807e;
        font-size: 10px;
        font-weight: 700;
      }
      .printed-name-label {
        padding-top: 4px;
      }
      .footer {
        margin-top: 18px;
        border-top: 1px solid #e5e7eb;
        padding-top: 7px;
        text-align: center;
        color: #8b9796;
        font-size: 9px;
      }
      @page {
        size: letter;
        margin: 0.45in;
      }
      @media print {
        html, body {
          background: #ffffff;
          padding: 0;
        }
        .print-sheet {
          width: auto;
          min-height: 0;
          margin: 0;
          padding: 0;
          box-shadow: none;
        }
        .section-title,
        th,
        .total-row-strong {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    </style>
  </head>
  <body>
    <main class="print-sheet">
      <header class="letterhead">
        <div class="school-block">
          ${logoMarkup}
          <div>
            <h2 class="school-name">${escapeContractHtml(model.schoolName)}</h2>
            <div class="school-meta">${escapeContractHtml(model.schoolAddress)}</div>
            <div class="school-meta">${escapeContractHtml([model.schoolPhone, model.schoolEmail].filter(Boolean).join(' | '))}</div>
          </div>
        </div>
        <div class="document-meta">
          <div>${escapeContractHtml(model.currentDateLabel)}</div>
          <div>${escapeContractHtml(model.academicYear)}</div>
        </div>
      </header>

      <h1>Tuition Contract</h1>

      <section class="intro-grid">
        <div class="info-box">
          <p class="label">Family</p>
          <div class="value">${escapeContractHtml(model.parentSalutation)}</div>
          <div class="muted">${familyAddress}</div>
        </div>
        <div class="info-box">
          <p class="label">Payment Plan</p>
          <div class="value">${escapeContractHtml(model.paymentPlanLabel)}</div>
          <div class="muted">${escapeContractHtml(model.installmentSummaryLabel)}</div>
        </div>
      </section>

      <div class="section-title">Student Summary</div>
      <table>
        <thead>
          <tr>
            <th>Student</th>
            <th>Grade</th>
            <th class="money">Tuition</th>
          </tr>
        </thead>
        <tbody>${studentRows}</tbody>
      </table>

      <div class="section-title">Fees</div>
      <table>
        <thead>
          <tr>
            <th>Fee</th>
            <th>Schedule</th>
            <th class="money">Amount</th>
          </tr>
        </thead>
        <tbody>${feeRows}</tbody>
      </table>

      <section class="closing-section">
        <div class="section-title">Family Totals</div>
        <section class="totals-box">
          <div class="totals-grid">${totalRows}</div>
        </section>

        <section class="signature-section">
          <div class="signature-field">
            <div class="signature-line"></div>
            <div class="signature-label">Parent/Guardian Signature</div>
            <div class="printed-name-line"></div>
            <div class="printed-name-label">Printed name</div>
          </div>
          <div class="signature-field">
            <div class="signature-line"></div>
            <div class="signature-label">School administrator signature</div>
            <div class="printed-name-line"></div>
            <div class="printed-name-label">Printed name</div>
          </div>
        </section>
      </section>

      <footer class="footer">
        ${escapeContractHtml([model.schoolAddress, model.schoolPhone, model.schoolEmail].filter(Boolean).join(' | '))}
      </footer>
    </main>
  </body>
</html>`;
};

const StaffTuitionContractPreview = ({
  model,
}: {
  model: StaffTuitionContractPreviewModel;
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [generatedPreviewUrl, setGeneratedPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(() => {
    if (!model.schoolLogo || model.schoolLogo.startsWith('data:')) return null;
    return model.schoolLogo;
  });

  useEffect(() => {
    if (!model.schoolLogo) {
      setLogoUrl(null);
      return undefined;
    }
    if (!model.schoolLogo.startsWith('data:')) {
      setLogoUrl(model.schoolLogo);
      return undefined;
    }
    const objectUrl = createImageObjectUrlFromDataUrl(model.schoolLogo);
    setLogoUrl(objectUrl);
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [model.schoolLogo]);

  const printHtml = useMemo(
    () => buildStaffTuitionContractPrintHtml(model, logoUrl),
    [model, logoUrl],
  );
  const previewUrl = generatedPreviewUrl;

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setPreviewError(null);
    buildAdmissionsTuitionContractPdfFromModel(model)
      .then((pdfBytes) => {
        if (!active) return;
        const arrayBuffer = new ArrayBuffer(pdfBytes.byteLength);
        new Uint8Array(arrayBuffer).set(pdfBytes);
        objectUrl = URL.createObjectURL(new Blob([arrayBuffer], { type: 'application/pdf' }));
        setGeneratedPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (!active) return;
        setGeneratedPreviewUrl(null);
        setPreviewError('PDF preview is temporarily unavailable. The contract can still be reviewed below.');
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [model]);

  const openPrintPreview = () => {
    setTimeout(() => {
      if (typeof window === 'undefined') return;
      if (previewUrl) {
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      const popup = window.open('', '_blank', 'noopener,noreferrer');
      if (!popup) return;
      popup.document.open();
      popup.document.write(printHtml);
      popup.document.close();
      popup.focus();
    }, 0);
  };

  const printEmbeddedPreview = () => {
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.focus();
    frameWindow.print();
  };

  return (
    <article
      data-testid="admissions-tuition-contract-print-preview-shell"
      className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-gray-50/70 px-5 py-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
            <FileText size={15} />
            <span>Contract Preview</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={printEmbeddedPreview}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-primary/20 bg-white px-3 py-2 text-xs font-bold text-primary transition-colors hover:bg-primary/5"
          >
            Print
          </button>
          <button
            type="button"
            onClick={openPrintPreview}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-irongrey transition-colors hover:bg-gray-50"
          >
            Open
          </button>
        </div>
      </div>
      {previewError ? (
        <div className="border-b border-amber-100 bg-amber-50 px-5 py-3 text-xs font-semibold text-amber-800">
          {previewError}
        </div>
      ) : null}
      <div className="bg-slate-100 p-4">
        <iframe
          ref={iframeRef}
          title="Tuition contract draft PDF preview"
          data-testid="admissions-tuition-contract-print-preview"
          src={previewUrl || undefined}
          srcDoc={previewUrl ? undefined : printHtml}
          className="h-[760px] w-full rounded-xl border border-slate-200 bg-white shadow-inner"
        />
      </div>
    </article>
  );
};

const formatContractHistoryDate = (value: unknown): string => {
  const normalized = normalizeText(value);
  if (!normalized) return 'Not recorded';
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return normalized;
  return parsed.toLocaleString('en-US');
};

const StaffTuitionContractHistoryRow = ({
  applicationId,
  version,
  isActive,
}: {
  applicationId: string;
  version: AdmissionsTuitionContractVersion;
  isActive: boolean;
}) => {
  const sourceDocumentId = normalizeText(version.documentId);
  const signedDocumentId = normalizeText(version.signedDocumentId);
  const sourceDownloadQuery = useDomainRouterQuery(
    'getApplicationDocumentDownloadUrl',
    { applicationId, documentId: sourceDocumentId, disposition: 'inline' },
    { skip: !applicationId || !sourceDocumentId },
  );
  const signedDownloadQuery = useDomainRouterQuery(
    'getApplicationDocumentDownloadUrl',
    { applicationId, documentId: signedDocumentId, disposition: 'inline' },
    { skip: !applicationId || !signedDocumentId },
  );
  const sourceUrl = normalizeText(sourceDownloadQuery.data?.downloadUrl);
  const signedUrl = normalizeText(signedDownloadQuery.data?.downloadUrl);
  const sourceFileName =
    normalizeText(sourceDownloadQuery.data?.fileName)
    || `tuition-contract-${normalizeText(version.version).replace(/[^a-z0-9-]+/gi, '-').toLowerCase() || 'version'}.pdf`;
  const signedFileName =
    normalizeText(signedDownloadQuery.data?.fileName)
    || `signed-tuition-contract-${normalizeText(version.version).replace(/[^a-z0-9-]+/gi, '-').toLowerCase() || 'version'}.pdf`;

  const openUrl = async (url: string) => {
    if (!url || typeof window === 'undefined') return;
    const response = await fetch(url);
    if (!response.ok) return;
    const sourceBlob = await response.blob();
    const objectUrl = URL.createObjectURL(new Blob([sourceBlob], { type: sourceBlob.type || 'application/pdf' }));
    window.open(objectUrl, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  };

  const downloadUrl = async (url: string, fileName: string) => {
    if (!url) return;
    const response = await fetch(url);
    if (!response.ok) return;
    const blob = await response.blob();
    triggerBlobDownload(blob, fileName);
  };

  return (
    <div className="grid gap-3 rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-3 text-sm text-irongrey md:grid-cols-[1fr_auto] md:items-center">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-bold">{formatStatusLabel(version.status)} contract</span>
          {isActive ? (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold uppercase text-primary">Active</span>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-irongrey/60">
          Generated {formatContractHistoryDate(version.generatedAt)}
          {version.sentAt ? ` · Sent ${formatContractHistoryDate(version.sentAt)}` : ''}
          {version.signedAt ? ` · Signed ${formatContractHistoryDate(version.signedAt)}` : ''}
        </p>
        <p className="mt-1 break-all text-[11px] text-irongrey/40">{version.version}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!sourceUrl}
          onClick={() => {
            void openUrl(sourceUrl);
          }}
          className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-irongrey transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {signedDocumentId ? 'Open sent' : 'Open'}
        </button>
        <button
          type="button"
          disabled={!sourceUrl}
          onClick={() => {
            void downloadUrl(sourceUrl, sourceFileName);
          }}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 text-xs font-bold text-primary transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download size={14} />
          {signedDocumentId ? 'Download sent' : 'Download'}
        </button>
        {signedDocumentId ? (
          <>
            <button
              type="button"
              disabled={!signedUrl}
              onClick={() => {
                void openUrl(signedUrl);
              }}
              className="inline-flex items-center justify-center rounded-xl border border-jungle/20 bg-jungle/10 px-3 py-2 text-xs font-bold text-jungle transition-colors hover:bg-jungle/15 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Open signed
            </button>
            <button
              type="button"
              disabled={!signedUrl}
              onClick={() => {
                void downloadUrl(signedUrl, signedFileName);
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-jungle/20 bg-jungle/10 px-3 py-2 text-xs font-bold text-jungle transition-colors hover:bg-jungle/15 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={14} />
              Download signed
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

const badgeClassName = (tone: 'success' | 'warning' | 'info') => {
  if (tone === 'success') return 'bg-jungle/10 text-jungle';
  if (tone === 'warning') return 'bg-strawberry/10 text-strawberry';
  return 'bg-primary/10 text-primary';
};

const SETTINGS_TUITION_ROUTE = '/school/settings/admissions';

const CurrencyInput = ({
  label,
  valueCents,
  onChange,
  disabled,
  overridden,
  onReset,
  note,
}: {
  label: string;
  valueCents: number;
  onChange: (nextValue: string) => void;
  disabled?: boolean;
  overridden?: boolean;
  onReset?: (() => void) | null;
  note?: string | null;
}) => (
  <label className="flex flex-col gap-1.5">
    <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-irongrey/45">
      {label}
      {overridden ? <span className="h-2 w-2 rounded-full bg-primary" title="Custom value" /> : null}
    </span>
    <div className="flex items-center gap-2">
      <input
        type="number"
        min="0"
        step="0.01"
        disabled={disabled}
        value={(valueCents / 100).toFixed(2)}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-irongrey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:cursor-not-allowed disabled:bg-gray-50"
      />
      {overridden && onReset ? (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-primary/20 bg-primary/5 text-primary transition-colors hover:bg-primary/10"
          title="Reset to default"
        >
          <RotateCcw size={15} />
        </button>
      ) : null}
    </div>
    {note ? <span className="text-xs text-irongrey/45">{note}</span> : null}
  </label>
);

const CompactCurrencyRow = ({
  label,
  valueCents,
  onChange,
  disabled,
  overridden,
  onReset,
  note,
}: {
  label: string;
  valueCents: number;
  onChange: (nextValue: string) => void;
  disabled?: boolean;
  overridden?: boolean;
  onReset?: (() => void) | null;
  note?: string | null;
}) => (
  <div className="grid grid-cols-1 gap-2 border-t border-gray-100 py-2.5 first:border-t-0 md:grid-cols-[minmax(0,1fr)_minmax(10rem,14rem)] md:items-center">
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-irongrey">{label}</p>
        {overridden ? <span className="h-2 w-2 rounded-full bg-primary" title="Custom value" /> : null}
      </div>
      {note ? <p className="mt-0.5 text-[11px] leading-4 text-irongrey/45">{note}</p> : null}
    </div>
    <label className="flex min-w-0 items-center gap-2 md:justify-end">
      <span className="sr-only">{label}</span>
      <input
        type="number"
        min="0"
        step="0.01"
        disabled={disabled}
        value={(valueCents / 100).toFixed(2)}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-irongrey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:cursor-not-allowed disabled:bg-gray-50"
      />
      {overridden && onReset ? (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/5 text-primary transition-colors hover:bg-primary/10"
          title="Reset to default"
          aria-label={`Reset ${label}`}
        >
          <RotateCcw size={14} />
        </button>
      ) : null}
    </label>
  </div>
);

const CompactPercentRow = ({
  label,
  enabled,
  percentBasisPoints,
  eligible,
  onToggle,
  onPercentChange,
}: {
  label: string;
  enabled: boolean;
  percentBasisPoints: number;
  eligible: boolean;
  onToggle: (enabled: boolean) => void;
  onPercentChange: (value: string) => void;
}) => (
  <div className="grid grid-cols-1 gap-2 border-t border-gray-100 py-2.5 first:border-t-0 md:grid-cols-[minmax(0,1fr)_minmax(15rem,18rem)] md:items-center">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-irongrey">{label}</p>
      <p className="mt-0.5 text-[11px] leading-4 text-irongrey/45">
        {eligible ? 'Applies to tuition only.' : 'Not eligible for the current rate category.'}
      </p>
    </div>
    <div className="flex min-w-0 items-center gap-2 md:justify-end">
      <label className="inline-flex h-10 flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-irongrey">
        <input
          type="checkbox"
          checked={enabled}
          disabled={!eligible}
          onChange={(event) => onToggle(event.target.checked)}
          aria-label={`${label} enabled`}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed"
        />
        On
      </label>
      <label className="min-w-0 flex-1">
        <span className="sr-only">{label} percent</span>
        <input
          type="number"
          min="0"
          max="100"
          step="0.01"
          disabled={!eligible || !enabled}
          value={(percentBasisPoints / 100).toFixed(2)}
          onChange={(event) => onPercentChange(event.target.value)}
          aria-label={`${label} percent`}
          className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-irongrey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:cursor-not-allowed disabled:bg-gray-50"
        />
      </label>
    </div>
  </div>
);

const buildFeeEditorDescription = (fee: AdmissionsTuitionFeeLine): string => {
  const parts = [
    normalizeText(fee.amount.note),
    normalizeText(fee.rateDescription),
    fee.includedInChildName ? `included in ${fee.includedInChildName}'s total` : '',
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' • ') : 'Configured fee';
};

const TuitionSetupRequiredCard = () => (
  <section
    data-testid="admissions-detail-tuition-setup-required"
    className="rounded-3xl border border-amber-200 bg-amber-50/70 p-8 shadow-card"
  >
    <div className="max-w-3xl">
      <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-widest text-amber-800">
        <AlertCircle size={14} />
        Tuition Setup Required
      </div>
      <h2 className="mt-5 text-2xl font-bold text-irongrey">
        Tuition setup is required before contracts can be prepared
      </h2>
      <p className="mt-3 text-sm leading-6 text-irongrey/70">
        Add tuition categories, payment plans, and a default category in Admissions settings.
        Once those defaults are saved, this tab will load the family&apos;s editable tuition draft.
      </p>
      <div className="mt-5 rounded-2xl border border-amber-200/70 bg-white/80 p-5">
        <p className="text-xs font-bold uppercase tracking-widest text-irongrey/45">
          Setup Checklist
        </p>
        <ol className="mt-3 space-y-2 text-sm leading-6 text-irongrey/75">
          <li>1. Add at least one tuition rate category.</li>
          <li>2. Enable at least one tuition payment plan.</li>
          <li>3. Pick a default tuition category.</li>
          <li>4. Return to this admissions tuition tab and continue editing.</li>
        </ol>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <a
          href={SETTINGS_TUITION_ROUTE}
          data-testid="admissions-detail-tuition-setup-open-settings"
          className="inline-flex items-center rounded-full border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100"
        >
          Open admissions settings
        </a>
      </div>
    </div>
  </section>
);

export const TuitionTab: React.FC = () => {
  const {
    applicationId,
    detailStatus,
    financialAidAccessEnabled: detailFinancialAidAccessEnabled,
    onFinancialAidAccessChange,
  } = useOutletContext<DetailOutletContext>();
  const settingsQuery = useGetSettingsModuleConfigQuery({ moduleKey: 'staff_settings' });
  const settingsState = useMemo(() => readSettingsPayload(settingsQuery.data), [settingsQuery.data]);
  const categories = useMemo(
    () => settingsState.tuitionRateCategories ?? [],
    [settingsState.tuitionRateCategories],
  );
  const paymentPlans = useMemo(
    () => (settingsState.tuitionPaymentPlans ?? []).filter((plan) => plan.enabled),
    [settingsState.tuitionPaymentPlans],
  );
  const resolutionQuery = useDomainRouterQuery(
    'resolveFamilyForAdmissionsApplication',
    { applicationId },
    { skip: !applicationId },
  );
  const resolution = resolutionQuery.data as AdmissionsTuitionResolution | undefined;
  const configQuery = useDomainRouterQuery(
    'getAdmissionsTuitionConfiguration',
    { applicationId },
    { skip: !applicationId },
  );
  const persistedConfiguration =
    (configQuery.data?.configuration as AdmissionsTuitionConfiguration | null | undefined)
    ?? null;
  const applicationQuery = useDomainRouterQuery(
    'getApplication',
    { id: applicationId },
    { skip: !applicationId },
  );

  const [saveConfigurationMutation] = useDomainRouterMutation('upsertAdmissionsTuitionConfiguration');
  const [updateApplicationMutation, updateApplicationState] = useUpdateAdmissionsApplicationMutation();
  const [generateContractMutation, generateContractState] = useDomainRouterMutation('generateTuitionContract');
  const [sendContractMutation, sendContractState] = useDomainRouterMutation('sendTuitionContract');

  const [draftConfiguration, setDraftConfiguration] = useState<AdmissionsTuitionConfiguration | null>(null);
  const [draftDirty, setDraftDirty] = useState(false);
  const [lastSavedHash, setLastSavedHash] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [contractPopupFallbackUrl, setContractPopupFallbackUrl] = useState<string | null>(null);
  const [financialAidAccessEnabled, setFinancialAidAccessEnabled] = useState(false);
  const [financialAidAccessError, setFinancialAidAccessError] = useState<string | null>(null);
  const [financialAidAccessSaved, setFinancialAidAccessSaved] = useState(false);

  const draftRef = useRef<AdmissionsTuitionConfiguration | null>(null);
  const draftDirtyRef = useRef(draftDirty);
  const lastSavedHashRef = useRef(lastSavedHash);
  const hasTuitionRateCategories = categories.length > 0;
  const hasEnabledPaymentPlans = paymentPlans.length > 0;
  const hasSettingsTuitionDefaults = hasTuitionRateCategories && hasEnabledPaymentPlans;

  useEffect(() => {
    draftRef.current = draftConfiguration;
  }, [draftConfiguration]);

  useEffect(() => {
    draftDirtyRef.current = draftDirty;
  }, [draftDirty]);

  useEffect(() => {
    lastSavedHashRef.current = lastSavedHash;
  }, [lastSavedHash]);

  useEffect(() => {
    if (!applicationQuery.data && detailFinancialAidAccessEnabled === undefined) {
      return;
    }
    setFinancialAidAccessEnabled(
      detailFinancialAidAccessEnabled !== undefined
        ? detailFinancialAidAccessEnabled
        : isFinancialAidAccessEnabledForApplication(applicationQuery.data),
    );
    setFinancialAidAccessError(null);
    setFinancialAidAccessSaved(false);
  }, [applicationQuery.data, detailFinancialAidAccessEnabled]);

  useEffect(() => {
    if (!resolution || !hasSettingsTuitionDefaults) {
      setDraftConfiguration(null);
      setDraftDirty(false);
      return;
    }

    const nextConfiguration = buildAdmissionsTuitionConfiguration({
      resolution,
      categories,
      paymentPlans,
      defaultRateCategoryId: settingsState.defaultTuitionRateCategoryId,
      previousConfiguration: persistedConfiguration,
    });
    const nextHash = JSON.stringify(nextConfiguration);
    const hasPersistedConfiguration = Boolean(persistedConfiguration);
    const current = draftRef.current;
    const currentKey = current
      ? [current.familyId, current.applicationId, current.academicYear].join(':')
      : '';
    const currentHash = current ? JSON.stringify(current) : '';
    const nextKey = [nextConfiguration.familyId, nextConfiguration.applicationId, nextConfiguration.academicYear].join(':');
    const hasUnsavedChanges =
      draftDirtyRef.current
      && Boolean(current)
      && currentKey === nextKey
      && currentHash !== lastSavedHashRef.current;

    if (!hasUnsavedChanges) {
      setDraftConfiguration(nextConfiguration);
      setDraftDirty(false);
    }

    setLastSavedHash(hasPersistedConfiguration ? nextHash : '');
    setSaveState(hasPersistedConfiguration ? 'saved' : 'idle');
    setSaveError(null);
  }, [
    categories,
    hasSettingsTuitionDefaults,
    paymentPlans,
    persistedConfiguration,
    resolution,
    settingsState.defaultTuitionRateCategoryId,
  ]);

  const persistConfiguration = React.useCallback(async (
    configurationOverride?: AdmissionsTuitionConfiguration | null,
  ) => {
    const configuration = configurationOverride ?? draftRef.current;
    if (!configuration || !applicationId) {
      return null;
    }

    setSaveState('saving');
    setSaveError(null);
    const payload = {
      applicationId,
      configuration,
    };

    try {
      const result = await saveConfigurationMutation({ input: payload });
      const savedConfiguration =
        result.configuration
          ? mergeContractHistoryFromSubmittedDraft(
            result.configuration as AdmissionsTuitionConfiguration,
            configuration,
          )
          : configuration;
      const configurationHash = JSON.stringify(configuration);
      const savedHash = JSON.stringify(savedConfiguration);
      const currentHashAtResolution = draftRef.current ? JSON.stringify(draftRef.current) : '';
      setDraftConfiguration((current) => {
        const currentHash = current ? JSON.stringify(current) : '';
        return currentHash === configurationHash ? savedConfiguration : current ?? savedConfiguration;
      });
      if (currentHashAtResolution === configurationHash) {
        setDraftDirty(false);
      }
      setLastSavedHash(savedHash);
      setSaveState('saved');
      return savedConfiguration;
    } catch (error) {
      const message = extractErrorMessage(error, 'Unable to save tuition configuration right now.');
      setSaveError(message);
      setSaveState('error');
      throw error;
    }
  }, [applicationId, saveConfigurationMutation]);

  useEffect(() => {
    if (!draftConfiguration || !draftDirty || draftConfiguration.children.length === 0) {
      return;
    }

    const draftHash = JSON.stringify(draftConfiguration);
    if (draftHash === lastSavedHash) {
      return;
    }

    setSaveState('pending');
    const timeout = window.setTimeout(() => {
      void persistConfiguration(draftConfiguration).catch(() => undefined);
    }, 900);

    return () => window.clearTimeout(timeout);
  }, [draftConfiguration, draftDirty, lastSavedHash, persistConfiguration]);

  const contractDocumentId = draftConfiguration?.contract.documentId ?? null;
  const contractDownloadQuery = useDomainRouterQuery(
    'getApplicationDocumentDownloadUrl',
    { applicationId, documentId: contractDocumentId ?? '', disposition: 'inline' },
    { skip: !applicationId || !contractDocumentId },
  );
  const contractDownloadUrl = normalizeText(contractDownloadQuery.data?.downloadUrl);
  const contractAppUrl = applicationId && contractDocumentId
    ? `/school/admissions/tuition/${applicationId}?contractDocumentId=${contractDocumentId}`
    : '';
  const contractSchoolInfo = useMemo(() => resolveSettingsSchoolInfo(settingsState), [settingsState]);
  const missingContractSchoolInfoFields = useMemo(
    () => getMissingContractSchoolInfoFields(contractSchoolInfo),
    [contractSchoolInfo],
  );
  const contractPreviewModel = useMemo(() => (
    draftConfiguration
      && missingContractSchoolInfoFields.length === 0
      ? buildStaffTuitionContractPreviewModel({
        configuration: draftConfiguration,
        application: applicationQuery.data,
        familyName: resolution?.family.familyName ?? '',
        schoolInfo: contractSchoolInfo,
      })
      : null
  ), [
    applicationQuery.data,
    contractSchoolInfo,
    draftConfiguration,
    missingContractSchoolInfoFields.length,
    resolution?.family.familyName,
  ]);
  const contractVersions = useMemo(() => (
    draftConfiguration
      ? getAdmissionsTuitionContractVersions(draftConfiguration)
        .sort((left, right) => {
          const leftTime = Date.parse(normalizeText(left.sentAt ?? left.generatedAt)) || 0;
          const rightTime = Date.parse(normalizeText(right.sentAt ?? right.generatedAt)) || 0;
          return rightTime - leftTime;
        })
      : []
  ), [draftConfiguration]);
  const hasContractHistory = Boolean(draftConfiguration?.contract.documentId || contractVersions.length > 0);

  const applyConfigurationUpdate = React.useCallback((
    updater: (draft: AdmissionsTuitionConfiguration) => void,
  ) => {
    if (!draftRef.current) return;
    setActionError(null);
    setDraftDirty(true);
    setDraftConfiguration((current) => {
      if (!current) return current;
      return updateAdmissionsTuitionConfiguration(current, categories, paymentPlans, updater);
    });
  }, [categories, paymentPlans]);

  const onFinancialAidAccessToggle = async (enabled: boolean) => {
    if (!applicationId) return;
    const previousEnabled = financialAidAccessEnabled;
    setFinancialAidAccessEnabled(enabled);
    onFinancialAidAccessChange?.(enabled);
    setFinancialAidAccessError(null);
    setFinancialAidAccessSaved(false);
    try {
      const result = await unwrapMutationResult(
        updateApplicationMutation({
          applicationId,
          updates: { financialAidEnabled: enabled },
        }),
      );
      const resultRecord = asRecord(result);
      const persistedEnabled = typeof resultRecord.financialAidEnabled === 'boolean'
        ? resultRecord.financialAidEnabled
        : enabled;
      setFinancialAidAccessEnabled(persistedEnabled);
      onFinancialAidAccessChange?.(persistedEnabled);
      setFinancialAidAccessSaved(true);
      void Promise.resolve(applicationQuery.refetch()).catch(() => undefined);
    } catch (error) {
      setFinancialAidAccessEnabled(previousEnabled);
      onFinancialAidAccessChange?.(previousEnabled);
      setFinancialAidAccessError(extractErrorMessage(error, 'Unable to update Financial Aid access right now.'));
    }
  };

  const updateChild = React.useCallback((
    childApplicationId: string,
    updater: (child: AdmissionsTuitionChildConfiguration) => void,
  ) => {
    applyConfigurationUpdate((draft) => {
      const child = draft.children.find((entry) => entry.childApplicationId === childApplicationId);
      if (!child) return;
      updater(child);
    });
  }, [applyConfigurationUpdate]);

  const onSaveDraft = async () => {
    if (!draftConfiguration) return;
    try {
      await persistConfiguration(draftConfiguration);
    } catch {
      // save state already set
    }
  };

  const onApproveDraft = async () => {
    if (!draftConfiguration) return;
    setActionError(null);
    try {
      const approvedConfiguration = approveAdmissionsTuitionConfiguration(draftConfiguration);
      const savedConfiguration = await persistConfiguration(approvedConfiguration);
      const nextConfiguration = savedConfiguration ?? approvedConfiguration;
      const nextHash = JSON.stringify(nextConfiguration);
      setDraftConfiguration(nextConfiguration);
      setDraftDirty(false);
      setLastSavedHash(nextHash);
      setSaveState('saved');
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Unable to approve the tuition draft right now.'));
    }
  };

  const onGenerateContract = async () => {
    if (!draftConfiguration || !applicationId) return;
    setActionError(null);
    setContractPopupFallbackUrl(null);
    try {
      const sourceConfiguration = isAdmissionsTuitionConfigurationApproved(draftConfiguration)
        ? draftConfiguration
        : approveAdmissionsTuitionConfiguration(draftConfiguration);
      const savedConfiguration = await persistConfiguration(sourceConfiguration);
      const result = await generateContractMutation({
        input: {
          applicationId,
        },
      });
      const nextConfiguration =
        (result.configuration as AdmissionsTuitionConfiguration | null | undefined)
        ?? savedConfiguration
        ?? sourceConfiguration;
      const nextHash = JSON.stringify(nextConfiguration);
      setDraftConfiguration(nextConfiguration);
      setLastSavedHash(nextHash);
      setSaveState('saved');
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Unable to generate the tuition contract right now.'));
    }
  };

  const onOpenContract = () => {
    if (!contractDownloadUrl) return;
    setActionError(null);
    setContractPopupFallbackUrl(null);
    if (typeof window === 'undefined') {
      setActionError('Unable to open the tuition contract in this environment.');
      return;
    }
    const opened = window.open(contractDownloadUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      setContractPopupFallbackUrl(contractDownloadUrl);
      setActionError('Pop-ups were blocked. Use the direct PDF link below or allow pop-ups for this site.');
    }
  };

  const onSendToFamily = async () => {
    if (!draftConfiguration || !applicationId) return;
    setActionError(null);
    setContractPopupFallbackUrl(null);
    if (!isAdmissionsTuitionConfigurationApproved(draftConfiguration)) {
      setActionError('Approve the tuition draft before sending it to the family.');
      return;
    }
    try {
      const savedConfiguration = await persistConfiguration(draftConfiguration);
      const result = await sendContractMutation({
        input: {
          applicationId,
        },
      });
      const nextConfiguration =
        (result.configuration as AdmissionsTuitionConfiguration | null | undefined)
        ?? savedConfiguration
        ?? draftConfiguration;
      const nextHash = JSON.stringify(nextConfiguration);
      setDraftConfiguration(nextConfiguration);
      setLastSavedHash(nextHash);
      setSaveState('saved');
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Unable to send the tuition contract right now.'));
    }
  };

  const financialAidAccessLoading = applicationQuery.isLoading && !applicationQuery.data;
  const financialAidAccessSaving = updateApplicationState.isLoading;
  const financialAidAccessStatusText = financialAidAccessLoading
    ? 'Checking Financial Aid access…'
    : financialAidAccessSaving
      ? 'Saving Financial Aid access…'
      : financialAidAccessError
        ? financialAidAccessError
        : financialAidAccessSaved
          ? 'Financial Aid access saved.'
          : financialAidAccessEnabled
            ? 'Financial Aid application is available in the parent portal.'
            : 'Financial Aid application is not available in the parent portal.';

  const headerStatus = useMemo(() => {
    if (!draftConfiguration) return { label: 'Loading', tone: 'info' as const };
    if (draftConfiguration.contract.status === 'sent') {
      return { label: 'Sent to Family', tone: 'success' as const };
    }
    if (draftConfiguration.contract.status === 'draft') {
      return { label: 'Draft Contract Generated', tone: 'info' as const };
    }
    if (isAdmissionsTuitionConfigurationApproved(draftConfiguration)) {
      return { label: 'Approved', tone: 'success' as const };
    }
    if (draftConfiguration.configurationComplete) {
      return { label: 'Needs Approval', tone: 'warning' as const };
    }
    return { label: 'Incomplete', tone: 'warning' as const };
  }, [draftConfiguration]);

  const saveStatusLabel = useMemo(() => {
    if (saveState === 'saving') return 'Saving…';
    if (saveState === 'saved') return 'All changes saved';
    if (saveState === 'pending') return 'Unsaved changes';
    if (saveState === 'error') return saveError || 'Unable to save';
    return 'Ready';
  }, [saveError, saveState]);

  const currentStatusLabel = formatStatusLabel(resolution?.currentStatus || detailStatus || 'current status');
  const currentCategory = draftConfiguration
    ? categories.find((category) => category.id === draftConfiguration.rateCategoryId) ?? null
    : null;
  const currentCategoryDescription = sanitizeTuitionCategoryDescription(currentCategory?.description);
  const draftApproved = draftConfiguration
    ? isAdmissionsTuitionConfigurationApproved(draftConfiguration)
    : false;
  const canApproveDraft = Boolean(
    draftConfiguration
    && draftConfiguration.configurationComplete
    && saveState !== 'saving',
  );
  const canGenerateContract = Boolean(
    draftConfiguration?.paymentPlan.paymentPlanId
    && draftConfiguration.configurationComplete
    && !generateContractState.isLoading,
  );
  const canSendContract = Boolean(
    draftConfiguration?.contract.documentId
    && draftConfiguration.contract.status === 'draft'
    && contractDownloadUrl
    && draftApproved
    && !draftDirty
    && !sendContractState.isLoading,
  );
  const contractReadinessMessage = useMemo(() => {
    if (!draftConfiguration) return null;
    if (!draftConfiguration.paymentPlan.paymentPlanId) {
      return 'Select a payment plan before approving or generating the contract.';
    }
    if (!draftConfiguration.configurationComplete) {
      return 'Finish the required tuition defaults before approving the draft.';
    }
    if (draftDirty || !draftApproved) {
      return draftConfiguration.contract.documentId || contractVersions.length > 0
        ? 'Changes detected. Save and approve the updated contract before sending it.'
        : 'Save and approve this contract draft before sending it.';
    }
    if (draftConfiguration.contract.documentId) {
      return 'The saved contract is ready to download, print, or send.';
    }
    return 'Ready to save and approve the contract.';
  }, [contractVersions.length, draftApproved, draftConfiguration, draftDirty]);
  const hasRouteLoadError = Boolean(settingsQuery.error || resolutionQuery.error || configQuery.error);
  const shouldShowTuitionSetupRequired = Boolean(
    resolution
    && resolution.acceptedChildren.length > 0
    && !hasSettingsTuitionDefaults
    && !hasRouteLoadError,
  );

  if (resolutionQuery.isLoading || settingsQuery.isLoading || (applicationId && configQuery.isLoading && !draftConfiguration)) {
    return (
      <section data-testid="admissions-detail-tuition-tab" data-tuition-state="loading" className="pt-4">
        <p className="text-sm text-irongrey/50">Loading tuition configuration…</p>
      </section>
    );
  }

  if (hasRouteLoadError || !resolution) {
    return (
      <section data-testid="admissions-detail-tuition-tab" data-tuition-state="error" className="pt-4">
        <SupportErrorMessage>
          Unable to load tuition configuration data.
        </SupportErrorMessage>
      </section>
    );
  }

  if (resolution.acceptedChildren.length === 0) {
    return (
      <section
        data-testid="admissions-detail-tuition-tab"
        data-tuition-state="waiting-for-acceptance"
        className="rounded-3xl border border-gray-100 bg-white p-8 shadow-card"
      >
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-primary">
            <FileText size={14} />
            Tuition
          </div>
          <h2 className="mt-5 text-2xl font-bold text-irongrey">Tuition configuration starts after acceptance</h2>
          <p className="mt-3 text-sm leading-6 text-irongrey/65">
            Tuition configuration is available after this application is moved to Accepted or Enrolled.
            Current status: {currentStatusLabel}.
          </p>
        </div>
      </section>
    );
  }

  if (shouldShowTuitionSetupRequired) {
    return (
      <section data-testid="admissions-detail-tuition-tab" data-tuition-state="setup-required" className="pt-4">
        <TuitionSetupRequiredCard />
      </section>
    );
  }

  if (!draftConfiguration) {
    return (
      <section data-testid="admissions-detail-tuition-tab" data-tuition-state="error" className="pt-4">
        <SupportErrorMessage>
          Unable to load tuition configuration data.
        </SupportErrorMessage>
      </section>
    );
  }

  return (
    <section
      data-testid="admissions-detail-tuition-tab"
      data-tuition-state="editor"
      className="flex flex-col gap-5 pb-10"
    >
      <div data-testid="admissions-detail-tuition-editor" className="contents">
        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card">
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[min(100%,34rem)] flex-[999_1_34rem] space-y-4">
              <div
                className="grid min-w-0 gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 12rem), 1fr))' }}
              >
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Rate Category</span>
                  <select
                    value={draftConfiguration.rateCategoryId}
                    onChange={(event) => {
                      if (!resolution) return;
                      if (!draftRef.current) return;
                      setActionError(null);
                      setDraftDirty(true);
                      setDraftConfiguration((current) => {
                        if (!current) return current;
                        return updateAdmissionsTuitionRateCategory(current, {
                          resolution,
                          categories,
                          paymentPlans,
                          rateCategoryId: event.target.value,
                        });
                      });
                    }}
                    className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-irongrey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Academic Year</span>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-semibold text-irongrey">
                    {draftConfiguration.academicYear}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Family Status</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${badgeClassName(headerStatus.tone)}`}>
                      {headerStatus.label}
                    </span>
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                      saveState === 'error'
                        ? 'bg-strawberry/10 text-strawberry'
                        : saveState === 'saved'
                          ? 'bg-jungle/10 text-jungle'
                          : 'bg-gray-100 text-irongrey/60'
                    }`}>
                      {saveStatusLabel}
                    </span>
                  </div>
                </div>
              </div>

              <div className="min-w-0 rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Application</p>
                <p className="mt-2 text-sm font-semibold text-irongrey">
                  {resolution.family.familyName}
                </p>
                <p className="mt-1 text-sm text-irongrey/60">
                  {resolution.acceptedChildren.length} {resolution.acceptedChildren.length === 1 ? 'student is' : 'students are'} included in this tuition draft.
                </p>
                {currentCategoryDescription ? (
                  <p className="mt-3 text-xs leading-5 text-irongrey/55">{currentCategoryDescription}</p>
                ) : null}
              </div>
            </div>

            <div className="min-w-[min(100%,22rem)] flex-[1_1_22rem] rounded-2xl border border-primary/15 bg-primary/5 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Financial Aid</p>
                  <p className="mt-2 text-sm font-semibold text-irongrey">
                    Enable Financial Aid application
                  </p>
                  <p className="mt-1 text-xs leading-5 text-irongrey/55">
                    When enabled, the parent can open and submit the Financial Aid application from their portal.
                  </p>
                </div>
                <label className="inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    role="switch"
                    aria-label="Enable Financial Aid application"
                    checked={financialAidAccessEnabled}
                    disabled={financialAidAccessLoading || financialAidAccessSaving}
                    onChange={(event) => {
                      void onFinancialAidAccessToggle(event.target.checked);
                    }}
                    className="sr-only"
                    data-testid="admissions-tuition-financial-aid-access-toggle"
                  />
                  <span
                    aria-hidden="true"
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                      financialAidAccessEnabled ? 'bg-primary' : 'bg-gray-300'
                    } ${financialAidAccessLoading || financialAidAccessSaving ? 'opacity-60' : ''}`}
                  >
                    <span
                      className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                        financialAidAccessEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </span>
                </label>
              </div>
              <p
                className={`mt-3 text-xs font-semibold ${financialAidAccessError ? 'text-strawberry' : 'text-irongrey/55'}`}
                data-testid="admissions-tuition-financial-aid-access-status"
              >
                {financialAidAccessStatusText}
              </p>
            </div>
          </div>
        </section>

      {draftConfiguration.children.map((child) => {
        const combinedDiscountCents =
          child.combinedDiscount.currentCents
          + child.staffDiscount.amountCents
          + child.chinuchDiscount.amountCents;
        return (
          <section
            key={child.childApplicationId}
            className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card"
          >
            <div className="flex flex-col gap-4 border-b border-gray-100 pb-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-xl font-bold text-irongrey">{child.name}</h3>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold text-irongrey/55">
                  <span className="rounded-full bg-primary/10 px-2.5 py-1 text-primary">{formatContractGradeLabel(child.gradeLabel)}</span>
                  <span className="rounded-full bg-gray-100 px-2.5 py-1">{child.divisionLabel}</span>
                  <span className="rounded-full bg-gray-100 px-2.5 py-1">{formatStatusLabel(child.status)}</span>
                </div>
              </div>
              <div className="rounded-2xl bg-gray-50/70 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Child Subtotal</p>
                <p className="mt-1 text-xl font-bold text-irongrey">{formatTuitionCurrency(child.subtotalCents)}</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
              <div className="space-y-6">
                <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-widest text-irongrey/50">Tuition</h4>
                      <p className="mt-1 text-sm text-irongrey/60">Base tuition is pre-populated from the selected rate category.</p>
                    </div>
                  </div>
                  <CurrencyInput
                    label="Base Tuition"
                    valueCents={child.baseTuition.currentCents}
                    overridden={child.baseTuition.overridden}
                    onChange={(value) => {
                      const cents = parseCurrencyInputToCents(value);
                      updateChild(child.childApplicationId, (draftChild) => {
                        draftChild.baseTuition.currentCents = cents;
                        draftChild.baseTuition.overridden = cents !== draftChild.baseTuition.defaultCents;
                      });
                    }}
                    onReset={child.baseTuition.overridden ? () => {
                      updateChild(child.childApplicationId, (draftChild) => {
                        draftChild.baseTuition.currentCents = draftChild.baseTuition.defaultCents;
                        draftChild.baseTuition.overridden = false;
                      });
                    } : null}
                  />
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                  <div className="mb-3">
                    <h4 className="text-sm font-bold uppercase tracking-widest text-irongrey/50">Fees</h4>
                    <p className="mt-1 text-sm text-irongrey/60">Per-family fees are allocated to a single child card and can be manually adjusted here.</p>
                  </div>
                  <div className="overflow-hidden rounded-2xl border border-white bg-white shadow-sm">
                    {child.fees.map((fee, feeIndex) => (
                      <div
                        key={fee.id}
                        className={`grid grid-cols-1 gap-3 px-4 py-3 md:grid-cols-[minmax(0,1fr)_minmax(8rem,10rem)_minmax(10rem,12rem)] md:items-center ${
                          feeIndex > 0 ? 'border-t border-gray-100' : ''
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-irongrey">{fee.name}</p>
                            {fee.perFamilyAllocatedHere ? (
                              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary">
                                Per Family
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-1 text-[11px] leading-4 text-irongrey/50">
                            {buildFeeEditorDescription(fee)}
                          </p>
                        </div>
                        <label className="flex min-w-0 items-center gap-2">
                          <span className="sr-only">{fee.name} fee amount</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={(fee.amount.currentCents / 100).toFixed(2)}
                            onChange={(event) => {
                              const cents = parseCurrencyInputToCents(event.target.value);
                              updateChild(child.childApplicationId, (draftChild) => {
                                const draftFee = findAdmissionsTuitionFee(draftChild, fee.id);
                                if (!draftFee) return;
                                draftFee.amount.currentCents = cents;
                                draftFee.amount.overridden = cents !== draftFee.amount.defaultCents;
                              });
                            }}
                            aria-label={`${fee.name} fee amount`}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-irongrey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                          />
                          {fee.amount.overridden ? (
                            <button
                              type="button"
                              onClick={() => {
                                updateChild(child.childApplicationId, (draftChild) => {
                                  const draftFee = findAdmissionsTuitionFee(draftChild, fee.id);
                                  if (!draftFee) return;
                                  draftFee.amount.currentCents = draftFee.amount.defaultCents;
                                  draftFee.amount.overridden = false;
                                });
                              }}
                              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/5 text-primary transition-colors hover:bg-primary/10"
                              title="Reset amount"
                              aria-label={`Reset ${fee.name} amount`}
                            >
                              <RotateCcw size={14} />
                            </button>
                          ) : null}
                        </label>
                        <label className="flex min-w-0 items-center gap-2">
                          <span className="sr-only">{fee.name} due date</span>
                          <TailwindDateField
                            value={normalizeText(fee.dueDate)}
                            aria-label={`${fee.name} due date`}
                            onChange={(event) => {
                              updateChild(child.childApplicationId, (draftChild) => {
                                const draftFee = findAdmissionsTuitionFee(draftChild, fee.id);
                                if (!draftFee) return;
                                draftFee.dueDate = event.target.value || null;
                              });
                            }}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-irongrey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                          />
                          {normalizeText(fee.dueDate) !== normalizeText(fee.defaultDueDate) ? (
                            <button
                              type="button"
                              onClick={() => {
                                updateChild(child.childApplicationId, (draftChild) => {
                                  const draftFee = findAdmissionsTuitionFee(draftChild, fee.id);
                                  if (!draftFee) return;
                                  draftFee.dueDate = draftFee.defaultDueDate;
                                });
                              }}
                              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/5 text-primary transition-colors hover:bg-primary/10"
                              title="Reset due date"
                              aria-label={`Reset ${fee.name} due date`}
                            >
                              <RotateCcw size={14} />
                            </button>
                          ) : null}
                        </label>
                        {fee.exemptReason ? (
                          <p className="text-[11px] text-irongrey/45 md:col-start-2 md:col-span-2">
                            {fee.exemptReason}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                  <div className="mb-3">
                    <h4 className="text-sm font-bold uppercase tracking-widest text-irongrey/50">Discounts & Credits</h4>
                    <p className="mt-1 text-sm text-irongrey/60">All credits recalculate in real time and flow into the family totals below.</p>
                  </div>
                  <div className="overflow-hidden rounded-2xl border border-white bg-white px-4 shadow-sm">
                    <CompactCurrencyRow
                      label="Multi-child Discount"
                      valueCents={child.multiChildDiscount.currentCents}
                      overridden={child.multiChildDiscount.overridden}
                      disabled={child.multiChildDiscount.note === 'Not eligible'}
                      note={child.multiChildDiscount.note}
                      onChange={(value) => {
                        const cents = parseCurrencyInputToCents(value);
                        updateChild(child.childApplicationId, (draftChild) => {
                          draftChild.multiChildDiscount.currentCents = cents;
                          draftChild.multiChildDiscount.overridden = cents !== draftChild.multiChildDiscount.defaultCents;
                        });
                      }}
                      onReset={child.multiChildDiscount.overridden ? () => {
                        updateChild(child.childApplicationId, (draftChild) => {
                          draftChild.multiChildDiscount.currentCents = draftChild.multiChildDiscount.defaultCents;
                          draftChild.multiChildDiscount.overridden = false;
                        });
                      } : null}
                    />

                    <CompactCurrencyRow
                      label="Financial Aid Credit"
                      valueCents={child.financialAidCredit.currentCents}
                      overridden={child.financialAidCredit.overridden}
                      disabled={child.financialAidCredit.note === 'Not eligible'}
                      note={child.financialAidCredit.note}
                      onChange={(value) => {
                        const cents = parseCurrencyInputToCents(value);
                        updateChild(child.childApplicationId, (draftChild) => {
                          draftChild.financialAidCredit.currentCents = cents;
                          draftChild.financialAidCredit.overridden = cents !== draftChild.financialAidCredit.defaultCents;
                        });
                      }}
                      onReset={child.financialAidCredit.overridden ? () => {
                        updateChild(child.childApplicationId, (draftChild) => {
                          draftChild.financialAidCredit.currentCents = draftChild.financialAidCredit.defaultCents;
                          draftChild.financialAidCredit.overridden = false;
                        });
                      } : null}
                    />

                    <CompactPercentRow
                      label="Staff Discount"
                      eligible={child.staffDiscount.eligible}
                      enabled={child.staffDiscount.enabled}
                      percentBasisPoints={child.staffDiscount.percentBasisPoints}
                      onToggle={(enabled) => {
                        updateChild(child.childApplicationId, (draftChild) => {
                          draftChild.staffDiscount.enabled = enabled;
                          draftChild.staffDiscount.overridden =
                            enabled || draftChild.staffDiscount.percentBasisPoints !== draftChild.staffDiscount.defaultPercentBasisPoints;
                        });
                      }}
                      onPercentChange={(value) => {
                        const percentBasisPoints = Math.max(0, Math.round(Number(value || 0) * 100));
                        updateChild(child.childApplicationId, (draftChild) => {
                          draftChild.staffDiscount.percentBasisPoints = percentBasisPoints;
                          draftChild.staffDiscount.overridden =
                            percentBasisPoints !== draftChild.staffDiscount.defaultPercentBasisPoints
                            || draftChild.staffDiscount.enabled;
                        });
                      }}
                    />

                    <CompactPercentRow
                      label="Chinuch Discount"
                      eligible={child.chinuchDiscount.eligible}
                      enabled={child.chinuchDiscount.enabled}
                      percentBasisPoints={child.chinuchDiscount.percentBasisPoints}
                      onToggle={(enabled) => {
                        updateChild(child.childApplicationId, (draftChild) => {
                          draftChild.chinuchDiscount.enabled = enabled;
                          draftChild.chinuchDiscount.overridden =
                            enabled || draftChild.chinuchDiscount.percentBasisPoints !== draftChild.chinuchDiscount.defaultPercentBasisPoints;
                        });
                      }}
                      onPercentChange={(value) => {
                        const percentBasisPoints = Math.max(0, Math.round(Number(value || 0) * 100));
                        updateChild(child.childApplicationId, (draftChild) => {
                          draftChild.chinuchDiscount.percentBasisPoints = percentBasisPoints;
                          draftChild.chinuchDiscount.overridden =
                            percentBasisPoints !== draftChild.chinuchDiscount.defaultPercentBasisPoints
                            || draftChild.chinuchDiscount.enabled;
                        });
                      }}
                    />

                    <div className="grid grid-cols-1 gap-2 border-t border-gray-100 py-2.5 md:grid-cols-[minmax(0,1fr)_minmax(10rem,14rem)] md:items-center">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-irongrey">Combined Staff / Chinuch</p>
                        <p className="mt-0.5 text-[11px] leading-4 text-irongrey/45">
                          Automatically capped at 50% when both discounts are active.
                        </p>
                      </div>
                      <p className="text-sm font-bold text-irongrey md:text-right">{formatTuitionCurrency(combinedDiscountCents)}</p>
                    </div>

                    {child.fundraising.enabled ? (
                      <div className="grid grid-cols-1 gap-2 border-t border-gray-100 py-2.5 md:grid-cols-[minmax(0,1fr)_minmax(15rem,18rem)] md:items-center">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-irongrey">Fundraising Obligation</p>
                          <p className="mt-0.5 text-[11px] leading-4 text-irongrey/45">Appears only for rate categories with fundraising enabled.</p>
                        </div>
                        <div className="flex min-w-0 items-center gap-2 md:justify-end">
                          <label className="inline-flex h-10 flex-shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-irongrey">
                            <input
                              type="checkbox"
                              checked={child.fundraising.exempt}
                              onChange={(event) => {
                                updateChild(child.childApplicationId, (draftChild) => {
                                  draftChild.fundraising.exempt = event.target.checked;
                                });
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            Exempt
                          </label>
                          <label className="flex min-w-0 flex-1 items-center gap-2">
                            <span className="sr-only">Fundraising Amount</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              disabled={child.fundraising.exempt}
                              value={(child.fundraising.amount.currentCents / 100).toFixed(2)}
                              onChange={(event) => {
                                const cents = parseCurrencyInputToCents(event.target.value);
                                updateChild(child.childApplicationId, (draftChild) => {
                                  draftChild.fundraising.amount.currentCents = cents;
                                  draftChild.fundraising.amount.overridden = cents !== draftChild.fundraising.amount.defaultCents;
                                });
                              }}
                              aria-label="Fundraising Amount"
                              className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-irongrey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:cursor-not-allowed disabled:bg-gray-50"
                            />
                            {child.fundraising.amount.overridden ? (
                              <button
                                type="button"
                                onClick={() => {
                                  updateChild(child.childApplicationId, (draftChild) => {
                                    draftChild.fundraising.amount.currentCents = draftChild.fundraising.amount.defaultCents;
                                    draftChild.fundraising.amount.overridden = false;
                                  });
                                }}
                                className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/5 text-primary transition-colors hover:bg-primary/10"
                                title="Reset to default"
                                aria-label="Reset Fundraising Amount"
                              >
                                <RotateCcw size={14} />
                              </button>
                            ) : null}
                          </label>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <aside className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
                <p className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Card Summary</p>
                <div className="mt-4 space-y-3 text-sm text-irongrey/70">
                  <div className="flex items-center justify-between">
                    <span>Base tuition</span>
                    <span className="font-semibold text-irongrey">{formatTuitionCurrency(child.baseTuition.currentCents)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Fees</span>
                    <span className="font-semibold text-irongrey">
                      {formatTuitionCurrency(child.fees.reduce((sum, fee) => sum + fee.amount.currentCents, 0))}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Credits applied</span>
                    <span className="font-semibold text-strawberry">
                      -{formatTuitionCurrency(
                        child.multiChildDiscount.currentCents
                        + child.financialAidCredit.currentCents
                        + combinedDiscountCents,
                      )}
                    </span>
                  </div>
                  {child.fundraising.enabled && !child.fundraising.exempt ? (
                    <div className="flex items-center justify-between">
                      <span>Fundraising</span>
                      <span className="font-semibold text-irongrey">{formatTuitionCurrency(child.fundraising.amount.currentCents)}</span>
                    </div>
                  ) : null}
                </div>
              </aside>
            </div>
          </section>
        );
      })}

      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-bold text-irongrey">Family Totals</h3>
              <p className="mt-1 text-sm text-irongrey/60">These totals update automatically as you adjust child-level tuition, fees, and credits.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="flex items-center justify-between rounded-2xl bg-gray-50/70 px-4 py-3">
                <span className="text-sm text-irongrey/65">Tuition subtotal</span>
                <span className="text-sm font-semibold text-irongrey">{formatTuitionCurrency(draftConfiguration.totals.tuitionSubtotalCents)}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-gray-50/70 px-4 py-3">
                <span className="text-sm text-irongrey/65">Multi-child discount</span>
                <span className="text-sm font-semibold text-strawberry">-{formatTuitionCurrency(draftConfiguration.totals.multiChildDiscountCents)}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-gray-50/70 px-4 py-3">
                <span className="text-sm text-irongrey/65">Staff / Chinuch discount</span>
                <span className="text-sm font-semibold text-strawberry">-{formatTuitionCurrency(draftConfiguration.totals.staffChinuchDiscountCents)}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-primary/5 px-4 py-3">
                <span className="text-sm font-semibold text-primary">Total tuition</span>
                <span className="text-sm font-bold text-primary">{formatTuitionCurrency(draftConfiguration.totals.totalTuitionCents)}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-gray-50/70 px-4 py-3">
                <span className="text-sm text-irongrey/65">Total fees</span>
                <span className="text-sm font-semibold text-irongrey">{formatTuitionCurrency(draftConfiguration.totals.totalFeesCents)}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-gray-50/70 px-4 py-3">
                <span className="text-sm text-irongrey/65">Financial aid credit</span>
                <span className="text-sm font-semibold text-strawberry">-{formatTuitionCurrency(draftConfiguration.totals.financialAidCreditCents)}</span>
              </div>
              {draftConfiguration.totals.fundraisingObligationCents > 0 ? (
                <div className="flex items-center justify-between rounded-2xl bg-gray-50/70 px-4 py-3">
                  <span className="text-sm text-irongrey/65">Fundraising obligation</span>
                  <span className="text-sm font-semibold text-irongrey">{formatTuitionCurrency(draftConfiguration.totals.fundraisingObligationCents)}</span>
                </div>
              ) : null}
              <div className="flex items-center justify-between rounded-2xl border border-primary/15 bg-white px-4 py-3">
                <span className="text-sm font-semibold text-irongrey">Total obligation</span>
                <span className="text-lg font-bold text-irongrey">{formatTuitionCurrency(draftConfiguration.totals.totalObligationCents)}</span>
              </div>
            </div>
          </div>

          <aside className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
            <div className="mb-4">
              <h4 className="text-sm font-bold uppercase tracking-widest text-irongrey/50">Payment Plan</h4>
              <p className="mt-1 text-sm text-irongrey/60">Plan selection applies to the full family obligation.</p>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Payment Plan</span>
              <select
                value={draftConfiguration.paymentPlan.paymentPlanId ?? ''}
                onChange={(event) => {
                  const nextValue = normalizeText(event.target.value) || null;
                  if (!draftRef.current) return;
                  setActionError(null);
                  setDraftDirty(true);
                  setDraftConfiguration((current) => {
                    if (!current) return current;
                    return updateAdmissionsTuitionPaymentPlan(current, nextValue, categories, paymentPlans);
                  });
                }}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-irongrey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
              >
                <option value="">Select a payment plan</option>
                {paymentPlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-4 rounded-2xl border border-white bg-white p-4 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Installment Preview</p>
              {draftConfiguration.paymentPlan.paymentPlanId ? (
                <>
                  <p className="mt-2 text-sm font-semibold text-irongrey">
                    {draftConfiguration.paymentPlan.installmentCount} payments of {formatTuitionCurrency(draftConfiguration.paymentPlan.installmentAmountCents)}
                  </p>
                  <p className="mt-1 text-xs text-irongrey/55">
                    Final installment: {formatTuitionCurrency(draftConfiguration.paymentPlan.finalInstallmentAmountCents)}
                  </p>
                  <p className="mt-1 text-xs text-irongrey/55">
                    {draftConfiguration.paymentPlan.startDate} to {draftConfiguration.paymentPlan.endDate}
                  </p>
                </>
              ) : (
                <p className="mt-2 text-sm text-irongrey/55">No payment plan selected.</p>
              )}
            </div>
          </aside>
        </div>
      </section>

      {actionError || saveError ? (
        <SupportErrorMessage className="break-words">
          <span>{actionError || saveError}</span>
          {contractPopupFallbackUrl ? (
            <a
              className="ml-1 font-semibold underline"
              href={contractPopupFallbackUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open PDF directly
            </a>
          ) : null}
        </SupportErrorMessage>
      ) : null}

      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h3 className="text-lg font-bold text-irongrey">Contract Actions</h3>
            <p className="mt-1 text-sm text-irongrey/60">
              Generate the draft contract, preview it inline, download it, and send it to the family from here.
            </p>
          </div>
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 2xl:w-auto 2xl:flex 2xl:flex-wrap 2xl:items-center">
            <button
              type="button"
              onClick={onSaveDraft}
              className="inline-flex min-h-[3.25rem] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-center text-sm font-semibold leading-snug text-irongrey transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            >
              <Save size={16} />
              Save as Draft
            </button>
            <button
              type="button"
              onClick={onApproveDraft}
              disabled={!canApproveDraft || draftApproved}
              className="inline-flex min-h-[3.25rem] items-center justify-center gap-2 rounded-xl border border-jungle/20 bg-jungle/10 px-4 py-2.5 text-center text-sm font-semibold leading-snug text-jungle transition-colors hover:bg-jungle/15 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
            >
              <CheckCircle2 size={16} />
              {draftApproved ? 'Tuition Draft Approved' : 'Approve Tuition Draft'}
            </button>
            <button
              type="button"
              onClick={onGenerateContract}
              disabled={!canGenerateContract}
              className="inline-flex min-h-[3.25rem] items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-center text-sm font-semibold leading-snug text-white shadow-lg shadow-primary/20 transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
            >
              {hasContractHistory ? <RefreshCw size={16} /> : <FileText size={16} />}
              {generateContractState.isLoading
                ? 'Generating…'
                : draftConfiguration.contract.documentId && draftApproved && !draftDirty
                  ? 'Regenerate'
                  : hasContractHistory
                    ? 'Save & Approve Updates'
                    : 'Save and Approve Contract'}
            </button>
            <button
              type="button"
              onClick={onOpenContract}
              disabled={!contractDownloadUrl}
              className="inline-flex min-h-[3.25rem] items-center justify-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5 text-center text-sm font-semibold leading-snug text-primary transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
            >
              <ExternalLink size={16} />
              Open PDF
            </button>
            <button
              type="button"
              onClick={onSendToFamily}
              disabled={!canSendContract}
              className="inline-flex min-h-[3.25rem] items-center justify-center gap-2 rounded-xl border border-jungle/20 bg-jungle/10 px-4 py-2.5 text-center text-sm font-semibold leading-snug text-jungle transition-colors hover:bg-jungle/15 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
            >
              <Send size={16} />
              {sendContractState.isLoading ? 'Sending…' : 'Send to Family'}
            </button>
          </div>
        </div>

        {contractReadinessMessage ? (
          <div
            className={`mt-4 inline-flex max-w-full items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${
              draftApproved && !draftDirty && draftConfiguration.contract.documentId
                ? 'bg-jungle/10 text-jungle'
                : 'bg-amber-50 text-amber-800'
            }`}
            data-testid="admissions-tuition-contract-readiness"
          >
            {draftApproved && !draftDirty && draftConfiguration.contract.documentId
              ? <CheckCircle2 size={14} />
              : <AlertCircle size={14} />}
            <span>{contractReadinessMessage}</span>
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ${
            draftConfiguration.contract.status === 'sent'
              ? 'bg-jungle/10 text-jungle'
              : draftConfiguration.contract.status === 'draft'
                ? 'bg-primary/10 text-primary'
                : 'bg-gray-100 text-irongrey/60'
          }`}>
            {draftConfiguration.contract.status === 'sent' ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
            Contract status: {formatStatusLabel(draftConfiguration.contract.status)}
          </div>
          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ${
            draftApproved ? 'bg-jungle/10 text-jungle' : 'bg-strawberry/10 text-strawberry'
          }`}>
            {draftApproved ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
            Approval: {draftApproved ? 'Current draft approved' : 'Approval required'}
          </div>
          {draftConfiguration.contract.generatedAt ? (
            <span className="text-xs font-medium text-irongrey/55">
              Generated {new Date(draftConfiguration.contract.generatedAt).toLocaleString('en-US')}
            </span>
          ) : null}
          {draftConfiguration.contract.sentAt ? (
            <span className="text-xs font-medium text-irongrey/55">
              Sent {new Date(draftConfiguration.contract.sentAt).toLocaleString('en-US')}
            </span>
          ) : null}
        </div>

        {contractAppUrl ? (
          <p className="mt-3 break-all text-xs font-semibold text-primary">
            Authenticated link: <a className="underline" href={contractAppUrl}>{contractAppUrl}</a>
          </p>
        ) : null}

        {contractVersions.length > 0 ? (
          <div className="mt-5 space-y-3" data-testid="admissions-tuition-contract-history">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-irongrey/45">Contract History</h4>
              <p className="mt-1 text-xs text-irongrey/55">
                Each saved contract version is kept for audit history. Parents see the latest sent version.
              </p>
            </div>
            <div className="space-y-2">
              {contractVersions.map((version) => (
                <StaffTuitionContractHistoryRow
                  key={`${version.version}:${version.documentId ?? 'pending'}`}
                  applicationId={applicationId}
                  version={version}
                  isActive={normalizeText(version.version) === normalizeText(draftConfiguration.contract.version)}
                />
              ))}
            </div>
          </div>
        ) : null}

        {missingContractSchoolInfoFields.length > 0 ? (
          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
            <AlertCircle className="mt-0.5 shrink-0" size={16} />
            <div>
              <p className="font-bold">Complete school letterhead before previewing this contract.</p>
              <p className="mt-1 text-amber-800">
                Add the {missingContractSchoolInfoFields.join(', ')} in school settings, then regenerate the contract.
              </p>
            </div>
          </div>
        ) : contractPreviewModel ? (
          <StaffTuitionContractPreview model={contractPreviewModel} />
        ) : null}

        {draftConfiguration.contract.documentId && !contractDownloadUrl ? (
          <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-4 text-sm text-irongrey/60">
            {contractDownloadQuery.isLoading ? 'Preparing PDF link…' : 'Generated PDF link will appear here.'}
          </div>
        ) : null}
      </section>
      </div>
    </section>
  );
};
